import { supabase } from '../lib/supabase/client'

export interface EquipmentFormData {
  name: string
  serial_number: string
  part_number?: string
  description?: string
  model?: string
  location?: string
  company_id: string
  category_id: string
  manufacturer_id?: string
  status: string
  manufacture_year?: number
  owner_id?: string
}

export interface EquipmentCreationResult {
  success: boolean
  equipment?: any
  error?: string
}

export class ManualEquipmentCreationService {
  /**
   * Generate asset number using company-specific sequence
   */
  private static async generateAssetNumber(companyId: string): Promise<string> {
    try {
      const { data: assetNumber, error } = await supabase.rpc('generate_company_asset_number', {
        company_id_param: companyId
      })
      
      if (error) {
        console.error('RPC error:', error)
        throw new Error(`Asset number generation failed: ${error.message}`)
      }
      
      if (!assetNumber) {
        throw new Error('No asset number returned from RPC function')
      }
      
      return assetNumber
    } catch (error) {
      console.error('Error generating asset number:', error)
      // Fallback: generate a simple timestamp-based asset number
      const timestamp = Date.now().toString().slice(-6)
      return `MANUAL-${timestamp}`
    }
  }

  /**
   * Initialize specifications from category templates
   */
  private static async initializeSpecifications(equipmentId: string, categoryId: string, userId: string): Promise<void> {
    try {
      // Try the view first (recommended approach for canonical template system)
      let { data: templates, error: templatesError } = await supabase
        .from('specification_templates_with_categories')
        .select('*')
        .eq('category_id', categoryId)
        .eq('is_visible', true)
        .order('display_order', { ascending: true })

      // Fallback to category_specification_templates_v2 with canonical join
      if (templatesError || !templates || templates.length === 0) {
        const { data: mappings, error: mappingError } = await supabase
          .from('category_specification_templates_v2')
          .select(`
            *,
            template:canonical_specification_templates(*)
          `)
          .eq('category_id', categoryId)
          .eq('is_visible', true)
          .order('display_order', { ascending: true })

        if (!mappingError && mappings && mappings.length > 0) {
          templates = mappings.map(m => ({
            ...m.template,
            category_id: m.category_id,
            is_required: m.is_required,
            display_order: m.display_order
          }))
          templatesError = null
        }
      }

      // Final fallback: legacy specification_templates_v2 table
      if (templatesError || !templates || templates.length === 0) {
        const legacyResult = await supabase
          .from('specification_templates_v2')
          .select('*')
          .eq('category_id', categoryId)
          .eq('is_visible', true)
          .order('display_order', { ascending: true })
        
        templates = legacyResult.data
        templatesError = legacyResult.error
      }

      if (templatesError) {
        console.error('Error fetching templates:', templatesError)
        throw new Error(`Failed to fetch specification templates: ${templatesError.message}`)
      }

      if (!templates || templates.length === 0) {
        console.log('No specification templates found for category:', categoryId)
        return
      }

      // Create specification records for each template
      // Filter out templates without visible specifications first
      const specifications = templates
        .filter(template => template.is_visible !== false)
        .map(template => {
          // Base specification object
          const spec: any = {
            equipment_id: equipmentId,
            template_id: template.id,
            spec_key: template.spec_key,
            unit_id: template.default_unit_id,
            created_by: userId,
            updated_by: userId,
            validation_status: 'pending'
          }

          // Set ONLY the appropriate value column based on data_type
          // The constraint requires exactly ONE value column to be NOT NULL
          // So we only add the field that should be used, with a default if none provided
          switch (template.data_type) {
            case 'numeric':
            case 'decimal':
            case 'angle':
            case 'currency':
            case 'percentage':
              spec.value_numeric = template.default_value ? parseFloat(template.default_value) : 0
              break
            case 'integer':
            case 'year':
              spec.value_integer = template.default_value ? parseInt(template.default_value) : 0
              break
            case 'boolean':
              spec.value_boolean = template.default_value ? template.default_value === 'true' : false
              break
            case 'date':
              spec.value_date = template.default_value || null
              // For dates, if no default, we'll use value_text as placeholder
              if (!spec.value_date) {
                delete spec.value_date
                spec.value_text = ''
              }
              break
            case 'reference':
              spec.value_reference_id = template.default_value || null
              // For references, if no default, use value_text as placeholder
              if (!spec.value_reference_id) {
                delete spec.value_reference_id
                spec.value_text = ''
              }
              break
            case 'text':
            case 'enum':
            default:
              spec.value_text = template.default_value || ''
              break
          }

          return spec
        })

      const { error: specsError } = await supabase
        .from('equipment_specifications_v2')
        .insert(specifications)

      if (specsError) {
        console.error('Error creating specifications:', specsError)
        throw new Error(`Failed to create specifications: ${specsError.message}`)
      }

      console.log(`Created ${specifications.length} specifications for equipment ${equipmentId}`)
    } catch (error) {
      console.error('Error initializing specifications:', error)
      // Don't throw - equipment creation succeeded, just log the error
    }
  }

  /**
   * Create equipment record manually
   */
  static async createEquipment(formData: EquipmentFormData): Promise<EquipmentCreationResult> {
    try {
      // Generate asset number
      const assetNumber = await this.generateAssetNumber(formData.company_id)
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      const userId = user?.id
      
      if (!userId) {
        throw new Error('User not authenticated')
      }
      
      // Create equipment record
      const equipmentData: any = {
        asset_number: assetNumber,
        serial_number: formData.serial_number,
        name: formData.name,
        description: formData.description,
        model: formData.model,
        part_number: formData.part_number,
        company_id: formData.company_id,
        category_id: formData.category_id,
        status: formData.status,
        location: formData.location,
        manufacture_year: formData.manufacture_year,
        owner_id: formData.owner_id,
        created_by: userId
      }
      
      // Only add manufacturer_id if provided
      if (formData.manufacturer_id) {
        equipmentData.manufacturer_id = formData.manufacturer_id
      }
      
      const { data: equipment, error: equipmentError } = await supabase
        .from('equipment_v2')
        .insert(equipmentData)
        .select('*')
        .single()
      
      if (equipmentError) {
        console.error('Equipment creation error:', equipmentError)
        throw new Error(`Failed to create equipment: ${equipmentError.message}`)
      }
      
      // Initialize specifications from category templates
      await this.initializeSpecifications(equipment.id, formData.category_id, userId)
      
      return {
        success: true,
        equipment
      }
    } catch (error) {
      console.error('Error creating equipment:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  }

  /**
   * Validate form data before submission
   */
  static validateFormData(formData: EquipmentFormData): { valid: boolean; errors: string[] } {
    const errors: string[] = []
    
    if (!formData.name.trim()) {
      errors.push('Equipment name is required')
    }
    
    if (!formData.serial_number.trim()) {
      errors.push('Serial number is required')
    }
    
    if (!formData.company_id) {
      errors.push('Company is required')
    }
    
    if (!formData.category_id) {
      errors.push('Category is required')
    }
    
    if (formData.manufacture_year) {
      const currentYear = new Date().getFullYear()
      if (formData.manufacture_year < 1900 || formData.manufacture_year > currentYear + 1) {
        errors.push('Manufacturing year must be between 1900 and ' + (currentYear + 1))
      }
    }
    
    return {
      valid: errors.length === 0,
      errors
    }
  }
}

